import { useCallback } from 'react';
import { useAppDispatch } from 'store/hooks.ts';
import { WSRoomNames } from '@/common/enum';

const WEBSOCKET_URL = import.meta.env.VITE_APP_PALMETTO_WEBSOCKET_URL;
const POLL_TIME = 10000; // How often to poll the connection state
const RETRY_TIME = 30 * 60 * 1000; // timeout for retry
const RECONNECT_DELAY = 5000; // delay before reconnecting

type CallbackFunction = (data: any, category: string, dispatch: any, userId: number) => void;
type WsConnectValues = {
  userid: number;
  groupid: number;
  superadmin: boolean;
  access_token: string;
};

let ws: WebSocket | null = null;
let connectionActive = false;
const joinedRooms: Record<string, boolean> = {};
let pollInterval: NodeJS.Timeout | null = null;
let timeoutInterval: NodeJS.Timeout | null = null;
let isWsReady: boolean | null = null;
let connectionCallbacks: (() => void)[] = [];
const roomCallbacks: Record<string, CallbackFunction> = {};
let wsConnectValues: WsConnectValues | null = null;
let connectionStateChangeCallback: ((isActive: boolean) => void) | null = null;

/**
 * Closes the shared socket and clears every piece of module state above.
 *
 * Exported as a plain function rather than through the hook because the callers
 * that need it -- `endSession()` and `logout()` in `utilities/logout.ts` -- are
 * not components.
 *
 * Ordering is load-bearing: `wsConnectValues` is cleared and `onclose` detached
 * *before* `close()`, because `onclose` schedules `retry()`, and `retry()`
 * reconnects from `wsConnectValues`. Closing first would open a fresh socket on
 * the very credential this is discarding.
 *
 * Until this existed, the full page reload in `logout()` was the only thing
 * that cleared any of it. A session ending without a reload left the socket
 * open and reconnecting every RECONNECT_DELAY on a dead credential, and left
 * the next user on a shared device inheriting the previous user's socket,
 * access token and `userid`.
 */
export const teardownWebSocket = () => {
  // Cleared first: `retry()` returns early without it, so a close event that
  // slips through cannot reconnect.
  wsConnectValues = null;

  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
  if (timeoutInterval) {
    clearTimeout(timeoutInterval);
    timeoutInterval = null;
  }

  if (ws) {
    ws.onopen = null;
    ws.onclose = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.close();
    ws = null;
  }

  connectionActive = false;
  isWsReady = null;
  connectionCallbacks = [];
  connectionStateChangeCallback = null;
  // Both are `const` bindings, so the keys are dropped rather than the objects
  // replaced. A stale room would otherwise be re-joined by `onopen` on the next
  // connection, under the next user's session.
  Object.keys(joinedRooms).forEach((room) => delete joinedRooms[room]);
  Object.keys(roomCallbacks).forEach((room) => delete roomCallbacks[room]);
};

export const useWebSocket = () => {
  const dispatch = useAppDispatch();

  const setConnectionStateChangeCallback = useCallback((func: (isActive: boolean) => void) => {
    if (func) {
      connectionStateChangeCallback = func;
    }
  }, []);

  const pollConnection = useCallback(() => {
    if (ws) {
      const status = ws.readyState;
      connectionActive = status === WebSocket.OPEN;
      if (status !== WebSocket.OPEN) {
        stopPolling();
      } else {
        ws.send(
          JSON.stringify({
            action: 'pingpong',
          })
        );
      }
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
    if (connectionStateChangeCallback) {
      connectionStateChangeCallback(false);
    }
  }, []);

  const retry = useCallback(() => {
    if (connectionActive) {
      return;
    }
    if (ws) {
      ws.close();
      ws = null;
    }
    connectionActive = false;
    if (timeoutInterval) {
      clearInterval(timeoutInterval);
      timeoutInterval = null;
    }
    stopPolling();

    if (wsConnectValues) {
      connect(
        wsConnectValues.userid,
        wsConnectValues.groupid,
        wsConnectValues.superadmin,
        wsConnectValues.access_token
      );
    }
  }, [connectionActive, stopPolling]);

  const connect = useCallback(
    async (userid: number, groupid: number, superadmin: boolean, access_token: string) => {
      wsConnectValues = { userid, groupid, superadmin, access_token };
      if (connectionActive) {
        return;
      }

      try {
        ws = new WebSocket(
          `${WEBSOCKET_URL}?userid=${userid}&groupid=${groupid}&superadmin=${superadmin}&access_token=${access_token}`
        );
      } catch (err) {
        connectionActive = false;
        return;
      }

      if (connectionStateChangeCallback) {
        connectionStateChangeCallback(true);
      }

      ws.onopen = () => {
        connectionActive = true;
        isWsReady = true;
        connectionCallbacks.forEach((callback) => callback());
        connectionCallbacks = [];
        if (Object.keys(joinedRooms).length > 0) {
          ws!.send(
            JSON.stringify({
              action: 'join',
              data: {
                room: Object.keys(joinedRooms),
              },
            })
          );
        }
      };

      ws.onclose = () => {
        connectionActive = false;
        isWsReady = false;
        stopPolling();
        if (timeoutInterval) {
          clearInterval(timeoutInterval);
          timeoutInterval = null;
        }

        setTimeout(() => {
          retry();
        }, RECONNECT_DELAY);
      };

      ws.onmessage = (msg) => {
        if (msg) {
          let parsed = null;
          try {
            parsed = JSON.parse(msg.data);
          } catch (err) {
            return;
          }

          if (parsed.room === 'pong') {
            return;
          }

          if (parsed.room && roomCallbacks[parsed.room]) {
            const category = parsed.room === WSRoomNames.DMA_APP_PARENT_ROOM_NAME_IA ? 'ia' : 'pa';
            roomCallbacks[parsed.room](
              parsed.data,
              category,
              dispatch,
              wsConnectValues?.userid || 0
            );
          } else {
            console.log('Room not in callbackRooms:', parsed.room);
          }
        }
      };

      pollInterval = setInterval(pollConnection, POLL_TIME);

      timeoutInterval = setTimeout(() => {
        retry();
      }, RETRY_TIME);
    },
    [connectionActive, pollConnection, retry, joinedRooms]
  );

  const joinRoom = useCallback(
    (room: string, parentRoom: string, callback: CallbackFunction) => {
      if (joinedRooms[room]) return;

      const joinAction = () => {
        if (ws && ws.readyState === 1) {
          // console.log('Joining Room:', room, parentRoom);
          ws.send(
            JSON.stringify({
              action: 'join',
              data: {
                room: [room],
              },
            })
          );
        }
      };
      // console.log('IS WS READY', isWsReady.current);
      if (isWsReady) {
        joinAction();
      } else {
        // console.log('WebSocket not ready, queuing join action for room:', room, parentRoom);
        connectionCallbacks.push(joinAction);
      }

      roomCallbacks[parentRoom] = callback;
      joinedRooms[room] = true;
    },
    [joinedRooms, isWsReady]
  );

  return {
    connect,
    retry,
    setConnectionStateChangeCallback,
    joinRoom,
    connectionActive,
  };
};
